/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InputText, Profile, Message, Spacer, Text, Loader,
} from '@myinterview/component-library';
import { useSearchParams } from 'react-router-dom';
import { validation } from '../utils/validation';
import { ICandidateDetails } from '../interfaces';

interface IProps {
  onInputDetails: (data: ICandidateDetails, updateFiled: string) => void;
  isLoading: boolean;
  companyId?: string;
}
interface ICandidateInputs {
  [name: string]: { valid: boolean, errorMsg: string, value: string },
}
interface IShowErrors {
  [name: string]: boolean;
}
export const CandidateDetails = ({ onInputDetails, isLoading, companyId }:IProps) => {
  const { t } = useTranslation();
  const [query] = useSearchParams();
  // Flag for BTB customisation (CSTMR-979)
  let isBTB = companyId === '4cxaqt7iunmxxdv30x8z68uu';
  const params = useRef<{ [k: string]: string; }>(
    {
      email: '',
      firstname: '',
      lastname: '',
    },
  );
  const [showErrors, setShowErrors] = useState<IShowErrors>({
    email: false,
    firstName: false,
    lastName: false,
  });
  const [candidateDetails, setCandidateDetails] = useState<ICandidateInputs>({
    email: { valid: false, errorMsg: '', value: '' },
    firstName: { valid: false, errorMsg: '', value: '' },
    lastName: { valid: false, errorMsg: '', value: '' },
  });
  const lastFieldUpdated = useRef<string>('');

  const showErrorsOnBlur = (name: string) => {
    setShowErrors((prev) => ({ ...prev, ...{ [name]: true } }));
  };
  const checkValidity = async (name: string, value: string) => {
    try {
      await validation[name].validate(value);
      return { isValid: true, msg: '' };
    } catch (err:any) {
      return { isValid: false, msg: err.errors[0] };
    }
  };
  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement> | { target: { name: string, value: string } },
  ) => {
    const { name, value } = event.target;
    lastFieldUpdated.current = name;
    isBTB = companyId === '4cxaqt7iunmxxdv30x8z68uu';
    const validity = await checkValidity(name, value);
    setCandidateDetails((prev) => ({
      ...prev,
      ...{ [name]: { value, valid: validity.isValid, errorMsg: validity.msg } },
      ...(isBTB && { lastName: { value: ' ', valid: true, errorMsg: '' } }),
    }));
  };
  useEffect(() => {
    params.current = Object.fromEntries([...query]);
    [{ name: 'firstName', value: params.current.firstname || '' },
      { name: 'lastName', value: params.current.lastname || '' },
      { name: 'email', value: params.current.email || '' }].forEach((el) => {
      handleChange({ target: el });
      el.value && showErrorsOnBlur(el.name);
    });
  }, [query]);
  useEffect(() => {
    onInputDetails({
      email: candidateDetails.email.value,
      firstName: candidateDetails.firstName.value,
      lastName: candidateDetails.lastName.value,
      isValid: Object.values(candidateDetails).every((v) => v.valid),
    }, lastFieldUpdated.current);
  }, [candidateDetails]);
  return (
    <div className="candidate-details dynamic-direction">
      <Spacer y={1.5} />
      {isLoading ? <Loader count={1} />
        : (<Text size="L-Semibold">{t('form.instruction')}</Text>)}
      <Spacer y={1} />
      <div className="candidate-details__username">
        <InputText
          isLoading={isLoading}
          error={!candidateDetails.firstName.valid && showErrors.firstName}
          errorMsg={showErrors.firstName ? t(candidateDetails.firstName.errorMsg) : ''}
          name="firstName"
          onBlur={() => showErrorsOnBlur('firstName')}
          onChange={handleChange}
          type="text"
          icon={<Profile />}
          defaultValue={params.current.firstname}
          placeholder={isBTB ? 'BTB Candidate Number *' : t('form.firstname')}
        />
        <Spacer x={1} />
        <InputText
          isLoading={isLoading}
          error={!candidateDetails.lastName.valid && showErrors.lastName}
          errorMsg={showErrors.lastName ? t(candidateDetails.lastName.errorMsg) : ''}
          name="lastName"
          onBlur={() => showErrorsOnBlur('lastName')}
          onChange={handleChange}
          type={isBTB ? 'hidden' : 'text'}
          icon={<Profile />}
          defaultValue={params.current.lastname}
          placeholder={t('form.lastname')}
        />
      </div>
      <Spacer y={1} />
      <InputText
        isLoading={isLoading}
        error={!candidateDetails.email.valid && showErrors.email}
        errorMsg={showErrors.email ? t(candidateDetails.email.errorMsg) : ''}
        name="email"
        onBlur={() => showErrorsOnBlur('email')}
        onChange={handleChange}
        type="email"
        icon={<Message />}
        defaultValue={params.current.email}
        placeholder={t('form.email')}
      />
      <Spacer y={1} />
    </div>
  );
};
